import { createContext, useContext, ReactNode, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrganizations, type Organization, type OrganizationMember } from '@/hooks/useOrganizations'
import { getLastAppPath } from '@/lib/persistRoute'

interface OrganizationContextType {
  currentOrg: Organization | null
  organizations: Organization[]
  loading: boolean
  loadError: 'timeout' | 'error' | null
  switchingOrg: boolean
  switchOrganization: (org: Organization) => void
  deleteOrganization: (orgId: string) => Promise<boolean>
  refreshOrganizations: () => Promise<void>
  createOrganization: (name: string, slug: string) => Promise<any>
  inviteUser: (orgId: string, email: string, role?: 'admin' | 'member') => Promise<boolean>
  getOrganizationMembers: (orgId: string) => Promise<OrganizationMember[]>
  removeMember: (orgId: string, memberId: string) => Promise<boolean>
  updateMemberRole: (orgId: string, memberId: string, newRole: 'admin' | 'member') => Promise<boolean>
  cancelInvite: (orgId: string, inviteId: string) => Promise<boolean>
  resendInvite: (params: { inviteId?: string; orgId: string; email: string }) => Promise<boolean>
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const {
    currentOrg,
    organizations,
    loading,
    loadError,
    switchOrganization: originalSwitchOrganization,
    deleteOrganization,
    fetchUserOrganizations,
    createOrganization,
    inviteUser,
    getOrganizationMembers,
    removeMember,
    updateMemberRole,
    cancelInvite,
    resendInvite,
  } = useOrganizations()

  const [switchingOrg, setSwitchingOrg] = useState(false)

  const refreshOrganizations = useCallback(async () => {
    await fetchUserOrganizations()
  }, [fetchUserOrganizations])

  const switchOrganization = useCallback(async (org: Organization) => {
    if (currentOrg?.id === org.id) return // Não faz nada se já é a org atual
    
    setSwitchingOrg(true)
    try {
      originalSwitchOrganization(org)
      const lastPath = getLastAppPath()
      navigate(lastPath || '/chat', { replace: true })
      await new Promise(resolve => setTimeout(resolve, 800))
    } finally {
      setSwitchingOrg(false)
    }
  }, [currentOrg?.id, originalSwitchOrganization, navigate])

  const value = useMemo(() => ({
    currentOrg,
    organizations,
    loading,
    loadError,
    switchingOrg,
    switchOrganization,
    deleteOrganization,
    refreshOrganizations,
    createOrganization,
    inviteUser,
    getOrganizationMembers,
    removeMember,
    updateMemberRole,
    cancelInvite,
    resendInvite,
  }), [
    currentOrg,
    organizations,
    loading,
    loadError,
    switchingOrg,
    switchOrganization,
    deleteOrganization,
    refreshOrganizations,
    createOrganization,
    inviteUser,
    getOrganizationMembers,
    removeMember,
    updateMemberRole,
    cancelInvite,
    resendInvite,
  ])

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganizationContext() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    // Durante hot reload, o contexto pode não estar disponível ainda
    // Retornar valores padrão em vez de lançar erro
    if (import.meta.env.DEV) {
      return {
        currentOrg: null,
        organizations: [],
        loading: true,
        loadError: null,
        switchingOrg: false,
        switchOrganization: () => {},
        deleteOrganization: async () => false,
        refreshOrganizations: async () => {},
        createOrganization: async () => null,
        inviteUser: async () => false,
        getOrganizationMembers: async () => [],
        removeMember: async () => false,
        updateMemberRole: async () => false,
        cancelInvite: async () => false,
        resendInvite: async () => false,
      } as OrganizationContextType
    }
    throw new Error('useOrganizationContext must be used within an OrganizationProvider')
  }
  return context
}
